import { useEffect, useMemo } from 'react';
import { NoShowRateByType } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { CategoryBarChartData, Chart } from '@frontend/charts';
import { i18next, useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { useAnalyticsOrgLocations } from '../../../../hooks';
import { DemoChip } from '../../../demo-chip';
import { InfoTipPopover } from '../../../info-tip-popover';
import { PatientsTypeComparisonText } from '../components';
import { useFetchNSROILocationsNoShowRate, useNSRRoiShallowStore } from '../hooks';
import { commonMultiAppearance } from './common-values';
import { CommonChartProps } from './types';

const labels = {
  existingPatients: i18next.t('Existing Patients', { ns: 'analytics' }),
  groupNameHeader: i18next.t('Location', { ns: 'analytics' }),
  newPatients: i18next.t('New Patients', { ns: 'analytics' }),
};

const colors = {
  existingPatients: theme.colors.primary20,
  newPatients: theme.colors.warning20,
};

const strokeColors = {
  existingPatients: theme.colors.primary40,
  newPatients: theme.colors.warning40,
};

// The keys used here are based on how BE sends the data.
const dataKeys: Record<string, string> = {
  'New Appt': 'newPatients',
  'Follow Up Appt': 'existingPatients',
};

export const NSRRoiLocationsComparisonPatientType = ({ isDemoAccount, onLoading }: CommonChartProps) => {
  const { t } = useTranslation('analytics');
  const { filters, setFilters } = useNSRRoiShallowStore('filters', 'setFilters');
  const { locationNames } = useAnalyticsOrgLocations({
    module: 'ROI',
  });

  const { data, isLoading } = useFetchNSROILocationsNoShowRate({
    noShowRateType: NoShowRateByType.PATIENT_TYPE,
    filters,
  });

  const chartData: CategoryBarChartData = useMemo(() => {
    return (
      data?.data?.locations?.reduce<CategoryBarChartData>((acc, { aggregates = [], locationId = '' }) => {
        // Create a temporary map for quick lookup of values from aggregates
        const aggregateValueMap = aggregates.reduce<Record<string, number>>((mapAcc, { name = '', value = 0 }) => {
          const chartKey = dataKeys[name];

          if (chartKey) {
            mapAcc[chartKey] = value;
          }

          return mapAcc;
        }, {});

        const orderedChartDataKeys = Object.values(dataKeys);

        // Build the location's data object ensuring the order of orderedChartDataKeys
        const locationData: Record<string, number> = {};
        orderedChartDataKeys.forEach((chartKey) => {
          locationData[chartKey] = aggregateValueMap[chartKey] || 0;
        });

        acc[locationId] = locationData;
        return acc;
      }, {}) || {}
    );
  }, [data?.data?.locations]);

  useEffect(() => {
    onLoading?.('patientsTypeChart', isLoading);
  }, [isLoading]);

  return (
    <Chart colors={colors} isLoading={isLoading} labels={{ ...labels, ...locationNames }} strokeColors={strokeColors}>
      <Chart.Header
        title={t('No-Show Rate by Patient Type')}
        infoTip={
          <InfoTipPopover>
            {t(
              'A detailed breakdown of no-show rates between new patients and existing patients, allowing practices to identify which group is more likely to miss appointments and tailor reminder or follow-up strategies to reduce no-shows'
            )}
          </InfoTipPopover>
        }
        leftElement={isDemoAccount && <DemoChip />}
      />
      <Chart.CategoryBarChart
        appearance={{
          ...commonMultiAppearance(filters),
          customCategoryNameRenderer: (category) => {
            switch (category) {
              case 'newPatients':
                return t('New Patients No-Show Rate');

              case 'existingPatients':
                return t('Existing Patients No-Show Rate');

              default:
                return undefined;
            }
          },
          customTooltipData: (groupId) => [
            {
              color: colors.newPatients,
              formattedValue: formatters.appendPercent(chartData[groupId]?.newPatients),
              id: 'newPatients',
              label: labels.newPatients,
              value: chartData[groupId]?.newPatients,
            },
            {
              color: colors.existingPatients,
              formattedValue: formatters.appendPercent(chartData[groupId]?.existingPatients),
              id: 'existingPatients',
              label: labels.existingPatients,
              value: chartData[groupId]?.existingPatients,
            },
          ],
          formatters: formatters.appendPercent,
          maxValues: {
            existingPatients: 100,
            newPatients: 100,
          },
          tooltipBottomContentRenderer: (data) => {
            return (
              <Text color='subdued' size='small' style={{ maxWidth: '230px' }}>
                <PatientsTypeComparisonText
                  difference={(data.newPatients ?? 0) - (data.existingPatients ?? 0)}
                  forTooltip
                />
              </Text>
            );
          },
        }}
        data={chartData}
        onClick={({ group }) => {
          setFilters({
            locationIds: [group],
          });
        }}
      />
      <Chart.Legends noop />
    </Chart>
  );
};
